import { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { addPostPath, type CardSlug } from "./rules/deepLinks";
import { useAddPostPageEnabled } from "./useAddPostPageEnabled";

/** Every "Add Post" button goes through here: the page when the switch is on, the old calendar modal when it is off. */
export interface UseAddPostNavigationReturn {
    enabled: boolean;
    openAddPost: (card?: CardSlug, campaignId?: string) => void;
}

export function useAddPostNavigation(): UseAddPostNavigationReturn {
    const enabled = useAddPostPageEnabled();
    const navigate = useNavigate();
    const openAddPost = useCallback(
        (card?: CardSlug, campaignId?: string) => {
            if (enabled) navigate(addPostPath(card, campaignId));
            else navigate("/", { state: { addPost: true } });
        },
        [enabled, navigate],
    );
    return { enabled, openAddPost };
}

/** Navigation state of a card opened from the grid. */
export const OPENED_FROM_GRID = { fromGrid: true } as const;

/**
 * Closes a card: to the calendar once posts were created, else back to the grid. A card opened from the
 * grid steps back, so the grid is not in the history twice. Runs once: the dialog can report a close again as it unmounts.
 */
export function useCloseAddPostCard(): (postsCreated: boolean) => void {
    const navigate = useNavigate();
    const fromGrid = (useLocation().state as { fromGrid?: unknown } | null)?.fromGrid === true;
    const closed = useRef(false);
    return useCallback(
        (postsCreated: boolean) => {
            if (closed.current) return;
            closed.current = true;
            if (postsCreated) navigate("/", { replace: true });
            else if (fromGrid) navigate(-1);
            else navigate(addPostPath(), { replace: true });
        },
        [navigate, fromGrid],
    );
}
